import { Injectable, signal, inject } from '@angular/core';
import { SupabaseService } from '@libs/shared';
import { User } from '@supabase/supabase-js';
import { Router } from '@angular/router';
import { LanguageService, AppLanguage } from '@libs/shared';

// Roles globales del sistema
export type UserRole = 'comercial' | 'propietario' | 'cliente';

// Estructura de membresía para gestión multi-local
export interface RestaurantMembership {
  restaurant_id: string;
  role: 'owner' | 'manager' | 'editor';
  restaurant?: {
    name: string;
    slug: string;
    logo_url?: string;
  };
}

export interface UserProfile {
  id: string;
  email: string;
  role: UserRole; // Rol a nivel de plataforma
  memberships: RestaurantMembership[]; // Lista de restaurantes que gestiona
  created_at: string;
}

/**
 * Servicio de autenticación y autorización refactorizado para Multi-Tenancy
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private supabase = inject(SupabaseService);
  private langService = inject(LanguageService);

  // Estados reactivos (Signals)
  private userProfile = signal<UserProfile | null>(null);
  private isLoading = signal<boolean>(true);

  readonly userProfile$ = this.userProfile.asReadonly();
  readonly isLoading$ = this.isLoading.asReadonly();

  /**
   * Obtiene el perfil completo, incluyendo rol global y membresías a restaurantes
   */
  async signOut() {
    await this.supabase.signOut();
    // Al cerrar sesión, el Guard nos mandará automáticamente a /auth
    window.location.href = '/auth';
  }

  /**
   * Actualiza el idioma del usuario en Supabase y en el servicio local
   */
  async updateUserLanguage(lang: AppLanguage) {
    try {
      const { error } = await this.supabase.updateUserMetadata({ language: lang });
      if (error) throw error;
      this.langService.setLanguage(lang);
    } catch (error) {
      console.error('Error updating user language:', error);
    }
  }

  /**
   * Comprueba si el usuario tiene acceso a la gestión de plataforma (Comercial)
   */
  isComerciante(): boolean {
    return this.userProfile()?.role === 'comercial';
  }

  /**
   * Comprueba si el usuario tiene al menos un restaurante a su cargo
   */
  hasManagedRestaurants(): boolean {
    const profile = this.userProfile();
    return (profile?.memberships && profile.memberships.length > 0) || this.isComerciante();
  }

  /**
   * Verifica si el usuario tiene un rol específico en el restaurante actual
   * Útil para protecciones granulares en el Panel de Admin
   */
  checkLocalRole(restaurantId: string, allowedRoles: string[]): boolean {
    if (this.isComerciante()) return true;

    const membership = this.userProfile()?.memberships.find(m => m.restaurant_id === restaurantId);
    return membership ? allowedRoles.includes(membership.role) : false;
  }

  /**
   * Cambia el rol global de un usuario
   */
  async cambiarRol(userId: string, nuevoRol: UserRole): Promise<boolean> {
    try {
      const { error } = await this.supabase.updateUserRole(userId, { role: nuevoRol });
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error al cambiar rol global:', error);
      return false;
    }
  }

  /**
   * Inicialización de sesión al arrancar la app
   */
  async initializeAuth(): Promise<void> {
    this.isLoading.set(true);

    // 1. Verificamos sesión actual al cargar
    await this.getUserProfile();

    // 2. Nos suscribimos a cambios futuros (Login/Logout)
    this.supabase.onAuthStateChange(async (user) => {
      if (user) {
        // Si hay usuario, recuperamos el perfil completo
        await this.getUserProfile();
      } else {
        // Si no hay usuario, limpiamos el estado
        this.userProfile.set(null);
        this.isLoading.set(false);
      }
    });
  }

  async getUserProfile(): Promise<UserProfile | null> {
    const user = await this.supabase.getCurrentUser();
    if (!user) {
      this.userProfile.set(null);
      this.isLoading.set(false);
      return null;
    }

    try {
      const [roleResponse, membershipResponse] = await Promise.all([
        this.supabase.getUserRole(user.id),
        this.supabase.getUserMemberships(user.id)
      ]);

      const profile: UserProfile = {
        id: user.id,
        email: user.email || '',
        role: roleResponse.data?.role || 'cliente',
        memberships: membershipResponse.data || [],
        created_at: roleResponse.data?.created_at
      };

      // Sincronizar idioma desde metadata si existe
      const userLang = user.user_metadata?.['language'] as AppLanguage;
      if (userLang) {
        this.langService.setLanguage(userLang, false);
      }

      this.userProfile.set(profile);
      return profile;
    } catch (error) {
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }
  async handlePostLoginNavigation() {
    const profile = await this.getUserProfile();
    const router = inject(Router);

    if (profile?.role === 'comercial') {
      router.navigate(['/admin/dashboard']);
    } else if (profile?.memberships && profile.memberships.length > 0) {
      router.navigate(['/admin/menu']);
    } else {
      // Usuario propietario nuevo sin locales
      router.navigate(['/setup']);
    }
  }
}